import { useState, useEffect, useRef } from 'react';
import { getAudienceSocket } from '../hooks/useAudienceState';
import NicknameBar from '../components/NicknameBar';

interface ExploreChallengeScreenProps {
  challengeId: string;
  config: {
    targetScore: number;
  };
  initialScore: number;
  targetScore: number;
}

interface FloatingBubble {
  id: number;
  found: boolean;
  emoji?: string;
  x: number;
  y: number;
}

let bubbleIdCounter = 0;

function ExploreChallengeScreen({
  challengeId,
  initialScore,
  targetScore,
}: ExploreChallengeScreenProps) {
  const [score, setScore] = useState(initialScore);
  const [bubbles, setBubbles] = useState<FloatingBubble[]>([]);
  const [isPressed, setIsPressed] = useState(false);
  const touchedRef = useRef(false);

  const progressPercent = Math.min(100, Math.round((score / targetScore) * 100));

  // Ascolta tick dal server
  useEffect(() => {
    const socket = getAudienceSocket();
    if (!socket) return;

    const handleTick = (data: { challengeId: string; score: number }) => {
      if (data.challengeId === challengeId) {
        setScore(data.score);
      }
    };

    socket.on('challenge:tick', handleTick);
    return () => {
      socket.off('challenge:tick', handleTick);
    };
  }, [challengeId]);

  // Ascolta risultato esplorazione individuale
  useEffect(() => {
    const socket = getAudienceSocket();
    if (!socket) return;

    const handleExploreResult = (data: {
      challengeId: string;
      found: boolean;
      item?: { emoji: string; name: string };
    }) => {
      if (data.challengeId !== challengeId) return;

      const id = ++bubbleIdCounter;
      // Posizione random attorno al centro
      const x = 30 + Math.random() * 40;
      const y = 15 + Math.random() * 20;

      setBubbles(prev => [...prev, {
        id,
        found: data.found,
        emoji: data.item?.emoji,
        x,
        y,
      }]);

      // Rimuovi dopo animazione
      setTimeout(() => {
        setBubbles(prev => prev.filter(b => b.id !== id));
      }, 1200);
    };

    socket.on('challenge:explore-result', handleExploreResult);
    return () => {
      socket.off('challenge:explore-result', handleExploreResult);
    };
  }, [challengeId]);

  const doTap = () => {
    const socket = getAudienceSocket();
    if (!socket) return;

    socket.emit('challenge:tap', { challengeId, variant: 'explore' });

    setIsPressed(true);
    setTimeout(() => setIsPressed(false), 100);
  };

  return (
    <div
      className="relative flex flex-col h-screen overflow-hidden select-none"
      style={{ touchAction: 'manipulation' }}
    >
      <NicknameBar />

      {/* Header: progress bar */}
      <div className="pt-12 px-4 pb-2 z-10 shrink-0">
        <div className="flex items-center justify-between mb-2">
          <span className="text-ink-light text-base font-heading">Oggetti trovati</span>
          <span className="text-ink-light text-base font-heading">{score}/{targetScore}</span>
        </div>
        <div className="progress-parchment">
          <div
            className="h-full bg-gradient-to-r from-teal-500 to-cyan-400 rounded-full transition-all duration-300"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      </div>

      {/* Area di gioco */}
      <div className="flex-1 relative flex items-center justify-center min-h-0">
        {/* Nuvolette floating */}
        {bubbles.map((bubble) => (
          <div
            key={bubble.id}
            className="absolute pointer-events-none"
            style={{
              left: `${bubble.x}%`,
              top: `${bubble.y}%`,
              transform: 'translate(-50%, -50%)',
              animation: 'exploreFloatUp 1.2s ease-out forwards',
              zIndex: 20,
            }}
          >
            {bubble.found ? (
              <div className="flex flex-col items-center">
                <span className="text-4xl">{bubble.emoji}</span>
                <span
                  className="font-bold text-xl text-yellow-300 whitespace-nowrap"
                  style={{ textShadow: '0 0 8px rgba(0,0,0,0.7)' }}
                >
                  +1
                </span>
              </div>
            ) : (
              <div
                className="bg-parchment-200/80 rounded-2xl px-3 py-1 border border-parchment-400/50"
                style={{
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                }}
              >
                <span className="text-ink-muted text-sm font-body">Niente...</span>
              </div>
            )}
          </div>
        ))}

        {/* Pulsante Esplora */}
        <button
          onTouchStart={(e) => {
            e.preventDefault();
            touchedRef.current = true;
            doTap();
          }}
          onMouseDown={(e) => {
            e.preventDefault();
            if (touchedRef.current) {
              touchedRef.current = false;
              return;
            }
            doTap();
          }}
          className={`
            flex flex-col items-center justify-center
            w-40 h-40 rounded-full
            bg-gradient-to-br from-teal-600 to-teal-800
            border-4 border-teal-400/60
            shadow-lg
            transition-transform duration-100
            ${isPressed ? 'scale-90' : 'scale-100'}
          `}
          style={{
            boxShadow: '0 0 30px rgba(20, 184, 166, 0.3), 0 8px 24px rgba(0,0,0,0.3)',
          }}
        >
          <svg
            width="48"
            height="48"
            viewBox="0 0 24 24"
            fill="none"
            stroke="white"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
            <circle cx="12" cy="12" r="3" />
          </svg>
          <span className="text-white font-heading font-bold text-lg mt-2">
            Esplora
          </span>
        </button>
      </div>

      <style>{`
        @keyframes exploreFloatUp {
          0% { transform: translate(-50%, -50%) translateY(0); opacity: 1; }
          70% { opacity: 1; }
          100% { transform: translate(-50%, -50%) translateY(-100px); opacity: 0; }
        }
      `}</style>
    </div>
  );
}

export default ExploreChallengeScreen;
